import { useEffect, useRef, useState, type ReactNode } from "react";
import { useApolloClient, useMutation } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { Button } from "@/components/design/Button";
import { Switch } from "@/components/design/Switch";
import { FileDropZone } from "@/components/common/FileDropZone";
import { DocumentView } from "@/components/documents/DocumentView";
import {
  CreateDocumentDocument,
  DocumentContentDocument,
  ExtractProfileFromDocumentDocument,
} from "@/graphql/generated/operations";
import { hasExtractedProposals, type CvImportPreview } from "@/components/save/cv-extraction-to-preview";
import type { ExtractionDoc } from "@/components/save/ExtractionDialog";
import { ExtractionDialogFrame } from "@/components/save/ExtractionDialogFrame";
import { ExtractionDocumentPicker } from "@/components/save/ExtractionDocumentPicker";
import { ingestFiles, type IngestedFile } from "@/lib/file-ingest";
import { hasDuplicateName } from "@/lib/duplicate-name";
import { validationResultMessage } from "@/lib/graphql-error";
import { LocalizedError, localizedMessageOf } from "@/lib/localized-error";
import { CV_DOCUMENT_ACCEPT, MAX_DOCUMENT_UPLOAD_BYTES } from "@/lib/upload-limits";
import { userErrorMessage } from "@/lib/user-error";
import { useCvImportStore } from "@/stores/cv-import-store";

// CV Extractor: one document - a new upload or one already in Documents - goes to the
// non-persisting `extractProfileFromDocument` (genesis #83), then the extraction is handed to the
// profile page, which opens the SAME in-line guided merge review the LinkedIn import uses. The
// profile is written there; the only write here is the optional upload into Documents, made once
// the extraction has gone through.
// The mutation takes bytes, not a document id, so a stored document is read back first.

const ACCEPTED_EXTENSIONS = CV_DOCUMENT_ACCEPT.split(",");

// The upload's types, not the Hardskill/Power analysis whitelist: the profile pipeline also reads DOC, TXT, RTF and HTML.
function isCvPickable(doc: ExtractionDoc): boolean {
  const name = (doc.fileName ?? doc.name).toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
}

// The analysis is an LLM document extraction, so it can be slow — but it must not
// hang forever. If it exceeds this, we abort the in-flight request and surface a
// retryable error rather than leaving the user stuck on a spinner with no way out.
const EXTRACT_TIMEOUT_MS = 120_000;
const ELAPSED_TICK_MS = 1_000;
const ABORT_TIP_AFTER_S = 20;

// m:ss — extraction is bounded by EXTRACT_TIMEOUT_MS (2 min), so no hour segment.
function formatElapsed(totalSeconds: number): string {
  return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, "0")}`;
}

interface ExtractResult {
  merge: CvImportPreview;
  fileName: string;
}

export function ProfileExtractionDialog({
  docs,
  groups = [],
  docsLoading = false,
  personId,
  onClose,
  onDocumentSaved,
}: {
  docs: readonly ExtractionDoc[];
  groups?: readonly { id: string; name: string }[];
  docsLoading?: boolean;
  // Needed to persist a new upload as a Document.
  personId: string | null;
  onClose: () => void;
  onDocumentSaved?: () => void;
}) {
  const intl = useIntl();
  const navigate = useNavigate();
  const apollo = useApolloClient();
  const stageCvImport = useCvImportStore((s) => s.start);
  const [extract] = useMutation(ExtractProfileFromDocumentDocument);
  const [createDocument] = useMutation(CreateDocumentDocument);

  const [upload, setUpload] = useState<IngestedFile | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [persistUploads, setPersistUploads] = useState(true);
  // An upload still being read already holds the run's only slot.
  const [reading, setReading] = useState(false);
  const [busy, setBusy] = useState(false);
  // While the upload is being stored the run can no longer be aborted, so an abort never leaves a document behind.
  const [saving, setSaving] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [result, setResult] = useState<ExtractResult | null>(null);
  // Set while an extraction is in flight so the user can abort it (Cancel while
  // analysing) and so a timeout can cancel the request instead of hanging.
  const abortRef = useRef<AbortController | null>(null);

  // The stock may still be paging in when Start is clicked, so the store-or-not decision reads it
  // after the extraction rather than from the render that started the run.
  const docsRef = useRef(docs);
  useEffect(() => {
    docsRef.current = docs;
  }, [docs]);
  const uploadNameTaken = upload !== null && hasDuplicateName(docs.map((d) => d.name), upload.name);
  const total = (upload ? 1 : 0) + (selectedId ? 1 : 0);
  const slotTaken = reading || total > 0;

  const onFiles = async (files: FileList) => {
    setError(null);
    setReading(true);
    const { accepted, rejected } = await ingestFiles(files, {
      maxBytes: MAX_DOCUMENT_UPLOAD_BYTES,
      accept: CV_DOCUMENT_ACCEPT,
    }).finally(() => setReading(false));
    if (rejected.length > 0) {
      const reason = rejected[0]!.reason;
      setError(
        reason === "tooLarge"
          ? intl.formatMessage({ id: "profileExtraction.fileTooLarge", defaultMessage: "File is too large (max. 10 MB)." })
          : intl.formatMessage({ id: "profileExtraction.unsupportedType", defaultMessage: "Unsupported file type." }),
      );
      return;
    }
    const file = accepted[0];
    if (!file) return;
    setUpload(file);
  };

  async function saveUpload(file: IngestedFile, ownerId: string) {
    const saveFailed = intl.formatMessage({
      id: "profileExtraction.saveFailed",
      defaultMessage: "The file could not be saved in Documents.",
    });
    const res = await createDocument({
      variables: {
        input: {
          data: {
            name: file.name,
            person: { id: ownerId },
            // A plain upload: the "Lebenslauf" tag marks generator CVs, not uploaded ones.
            tags: [],
            fileName: file.name,
            content: { mimeType: file.mimeType, content: file.content },
          },
          dryRun: false,
        },
      },
    }).catch((e: unknown) => {
      throw new LocalizedError(saveFailed, { cause: e });
    });
    const out = res.data?.createDocument;
    if (out?.__typename !== "CreateDocumentResult") {
      throw new LocalizedError(validationResultMessage(out, saveFailed));
    }
  }

  async function run() {
    if (busy || total === 0) return;
    setError(null);
    setSaveError(null);
    setElapsedSeconds(0);
    setBusy(true);
    const controller = new AbortController();
    abortRef.current = controller;
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, EXTRACT_TIMEOUT_MS);
    const context = { fetchOptions: { signal: controller.signal } };
    try {
      const stored = docs.find((d) => d.id === selectedId);
      // Unlike the Hardskill analysis, a same-named stored document is NOT analysed in place of
      // the drop: no evidence attaches to it here, and a re-uploaded CV is usually a newer
      // version under the old name. The name only decides whether the upload is stored again.
      const uploadToStore = !stored && persistUploads ? upload : null;
      if (uploadToStore && !personId) {
        // Said up front rather than after two minutes of extraction.
        throw new LocalizedError(
          intl.formatMessage({ id: "save.extraction.notSignedIn", defaultMessage: "Not signed in - upload not possible." }),
        );
      }
      let source: { content: string; fileName: string; displayName: string };
      if (stored) {
        const { data } = await apollo.query({
          query: DocumentContentDocument,
          variables: { id: stored.id },
          fetchPolicy: "network-only",
          context,
        });
        const content = data?.document?.content?.content;
        if (!content) {
          throw new LocalizedError(
            intl.formatMessage({
              id: "profileExtraction.documentUnavailable",
              defaultMessage: "The document could not be loaded.",
            }),
          );
        }
        source = { content, fileName: data?.document?.fileName?.trim() || stored.name, displayName: stored.name };
      } else if (upload) {
        source = { content: upload.content, fileName: upload.name, displayName: upload.name };
      } else {
        return;
      }
      const res = await extract({
        variables: { file: source.content, fileName: source.fileName },
        context,
      });
      const merge = res.data?.extractProfileFromDocument;
      if (!merge) {
        throw new Error(
          intl.formatMessage({ id: "profileExtraction.noResult", defaultMessage: "The document could not be analysed." }),
        );
      }
      // Stored only once the run has gone through, so an aborted, timed-out or failed run leaves
      // nothing in Documents. The timeout bounds the extraction, not this last write.
      clearTimeout(timer);
      if (uploadToStore && personId && !hasDuplicateName(docsRef.current.map((d) => d.name), uploadToStore.name)) {
        setSaving(true);
        try {
          await saveUpload(uploadToStore, personId);
          onDocumentSaved?.();
        } catch (e) {
          // The extraction itself succeeded, so it stays reviewable; only the storing is reported.
          setSaveError(localizedMessageOf(e) ?? userErrorMessage(intl, e));
        } finally {
          setSaving(false);
        }
      }
      setResult({ merge, fileName: source.displayName });
    } catch (e) {
      if (timedOut) {
        setError(
          intl.formatMessage({
            id: "profileExtraction.timeout",
            defaultMessage: "Analysis is taking too long. Please try again.",
          }),
        );
      } else if (controller.signal.aborted) {
        return; // user cancelled — the dialog is closing, so show nothing
      } else {
        setError(localizedMessageOf(e) ?? userErrorMessage(intl, e));
      }
    } finally {
      clearTimeout(timer);
      abortRef.current = null;
      // Keep the spinner only on a user abort (the dialog is closing); success,
      // error and timeout all return to an interactive state.
      if (!controller.signal.aborted || timedOut) setBusy(false);
    }
  }

  // Elapsed extraction counter. Compute from a monotonic start timestamp so a
  // throttled tab or system clock change does not drift the display.
  useEffect(() => {
    if (!busy) return;
    const startedAt = performance.now();
    const id = setInterval(() => {
      setElapsedSeconds(Math.floor((performance.now() - startedAt) / 1000));
    }, ELAPSED_TICK_MS);
    return () => clearInterval(id);
  }, [busy]);

  // Abort/close escape hatch: while analysing, the footer shows the same
  // bottom-left abort affordance as the LinkedIn importer. Aborting actually
  // cancels the request because this is a client mutation.
  const cancelOrClose = () => {
    abortRef.current?.abort();
    onClose();
  };

  const onReview = () => {
    // Defensive: the Review button only renders inside the `result != null`
    // branch, so this never fires with a null result — guard kept, coverage-ignored.
    /* v8 ignore next */
    if (!result) return;
    // Stash the raw extraction; the profile page builds the two-sided merge
    // preview (it has the loaded profile + reference lookups) and opens the
    // in-line guided review — the same one the LinkedIn import uses.
    stageCvImport(result.merge, result.fileName);
    onClose();
    navigate("/save/basics");
  };

  const hasProposals = result != null && hasExtractedProposals(result.merge);
  const persistLabel = intl.formatMessage({
    id: "save.extraction.persistUploads",
    defaultMessage: "Save uploaded files in Documents",
  });

  let footer: ReactNode = null;
  if (busy) {
    footer = (
      <div className="flex justify-end gap-2 border-t border-ink/5 px-5 py-4">
        <Button variant="secondary" onClick={cancelOrClose} disabled={saving} className="mr-auto">
          <FormattedMessage id="profileExtraction.abort" defaultMessage="Abort extraction" />
        </Button>
      </div>
    );
  } else if (!result) {
    footer = (
      <div className="flex items-center justify-between gap-3 border-t border-ink/10 px-5 py-3">
        <span className="text-smycles-label text-ink-muted">
          {total > 0 ? (
            <FormattedMessage
              id="save.extraction.selectedCount"
              defaultMessage="{total, plural, one {# document selected} other {# documents selected}}"
              values={{ total }}
            />
          ) : (
            <FormattedMessage id="save.extraction.nothingSelected" defaultMessage="Nothing selected" />
          )}
        </span>
        <Button
          id="extraction-start"
          variant="primary"
          onClick={() => void run()}
          disabled={total === 0 || reading}
          size="compact"
        >
          <FormattedMessage id="save.extraction.startAnalysis" defaultMessage="Start analysis" />
        </Button>
      </div>
    );
  }

  return (
    <ExtractionDialogFrame
      id="extraction"
      title={intl.formatMessage({ id: "profileExtraction.title", defaultMessage: "CV Extractor" })}
      onClose={onClose}
      preventClose={busy}
      dataGraphql="ExtractProfileFromDocument, DocumentContent, CreateDocument"
      footer={footer}
    >
      {busy ? (
        <div className="py-4">
          <div role="status" aria-live="polite">
            <p className="smycles-field-meta leading-snug text-ink">
              <FormattedMessage
                id="profileExtraction.analysing"
                defaultMessage="Extracting profile data from your document - this can take up to two minutes..."
              />
            </p>
          </div>
          {/* Deliberately aria-hidden and outside live regions so the
              per-second tick is not announced on every change. */}
          <p className="smycles-field-meta mt-2 tabular-nums text-ink-muted" aria-hidden="true">
            <FormattedMessage
              id="profileExtraction.elapsed"
              defaultMessage="Elapsed time: {time}"
              values={{ time: formatElapsed(elapsedSeconds) }}
            />
          </p>
          {/* Mounted empty from the start; the text toggles in after 20s so
              screen readers announce the delayed abort guidance. */}
          <p role="status" aria-live="polite" className="smycles-field-meta mt-2 leading-snug text-ink">
            {elapsedSeconds >= ABORT_TIP_AFTER_S && (
              <FormattedMessage
                id="profileExtraction.abortTip"
                defaultMessage="Still extracting - you can keep waiting, or abort and try again with the same document. Nothing has been saved yet."
              />
            )}
          </p>
        </div>
      ) : result ? (
        <>
          {hasProposals ? (
            <div className="space-y-4">
              <div className="text-smycles-meta text-ink">
                <FormattedMessage
                  id="profileExtraction.ready"
                  defaultMessage="We found details to review in {file}."
                  values={{ file: result.fileName }}
                />
              </div>
              <div className="flex justify-end">
                <Button id="extraction-review" variant="primary" size="compact" onClick={onReview}>
                  <FormattedMessage id="profileExtraction.review" defaultMessage="Review changes" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div id="extraction-nothing-new" className="text-smycles-meta text-ink">
                <FormattedMessage
                  id="profileExtraction.nothingNew"
                  defaultMessage="Nothing new found – your profile already covers this document."
                />
              </div>
              <div className="flex justify-end">
                <Button variant="secondary" size="compact" onClick={onClose}>
                  <FormattedMessage id="common.close" defaultMessage="Close" />
                </Button>
              </div>
            </div>
          )}
          {saveError ? <div className="text-smycles-meta text-ink-attention">{saveError}</div> : null}
        </>
      ) : (
        <>
          <div>
            <div className="smycles-field-label mb-1.5 text-ink-muted">
              <FormattedMessage id="save.extraction.newFile" defaultMessage="New file" />
            </div>
            <FileDropZone
              inputId="extraction-file"
              variant="zone"
              clickToOpen
              multiple={false}
              accept={CV_DOCUMENT_ACCEPT}
              // One document per run: a ticked stored document or a file still being read holds the only slot.
              disabled={selectedId !== null || reading}
              onFiles={(fl) => void onFiles(fl)}
              label={
                <FormattedMessage
                  id="profileExtraction.dropZone"
                  defaultMessage="Drag a document here or click to upload"
                />
              }
              hint={
                <FormattedMessage
                  id="profileExtraction.dropZoneHint"
                  defaultMessage="CV, LinkedIn export, application form … · PDF, DOC, DOCX, TXT, RTF, HTML, image · max. 10 MB"
                />
              }
            />

            <div className="mt-3 flex items-center justify-between gap-3">
              <span className="text-smycles-label text-ink-muted">{persistLabel}</span>
              <Switch id="extraction-persist" checked={persistUploads} onChange={setPersistUploads} ariaLabel={persistLabel} />
            </div>

            {upload ? (
              <div className="mt-2 box-flat bg-page px-3 py-1.5 text-smycles-meta">
                <div className="flex items-center justify-between gap-2">
                  <DocumentView variant="inline" file={upload} showName />
                  <Button
                    variant="icon"
                    size="sm"
                    onClick={() => setUpload(null)}
                    aria-label={intl.formatMessage({ id: "common.remove", defaultMessage: "Remove" })}
                    className="shrink-0"
                  >
                    ×{/* i18n-allow: close glyph, not copy */}
                  </Button>
                </div>
                {persistUploads && uploadNameTaken ? (
                  <div className="mt-0.5 text-smycles-micro text-ink-attention">
                    <FormattedMessage
                      id="profileExtraction.alreadyInDocuments"
                      defaultMessage="Already in Documents - it is not saved again."
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          <ExtractionDocumentPicker
            docs={docs}
            isPickable={isCvPickable}
            groups={groups}
            docsLoading={docsLoading}
            selectedIds={new Set(selectedId ? [selectedId] : [])}
            room={slotTaken ? 0 : 1}
            onToggle={(id) => setSelectedId((prev) => (prev === id ? null : id))}
            idPrefix="extraction"
            selectionIdPrefix="extraction-docselect"
          />

          {error ? <div id="extraction-error" className="text-smycles-meta text-danger">{error}</div> : null}
        </>
      )}
    </ExtractionDialogFrame>
  );
}
